import { useCallback, useState } from 'react';
import { endOfDay, endOfMonth, startOfDay } from 'date-fns';
import type { DateValue } from '../types';
import type { MonthCell } from '../components/MonthGridView/MonthGridView.types';

export interface MonthRangeStateOptions {
  value: DateValue;
  onChange: (value: DateValue) => void;
  onCancel: () => void;
  fixedNumberOfMonth?: number;
  hideButtons?: boolean;
  maxDate?: Date;
}

export interface MonthRangeState {
  draft: DateValue;
  menuYear: number;
  setMenuYear: (year: number) => void;
  rangePending: boolean;
  handleMonthClick: (m: MonthCell, menuYear: number) => void;
  handleMonthHover: (m: MonthCell, menuYear: number) => void;
  handlePresetChange: (from: Date | undefined, to: Date | undefined, presetValue: string, labelKey: string) => void;
  handleApply: () => void;
  handleCancel: () => void;
}

function calcFixedEnd(
  startYear: number,
  startMonth: number,
  fixedNumberOfMonth: number
): { year: number; month: number } {
  let endMonth = startMonth + (fixedNumberOfMonth - 1);
  let endYear = startYear;
  while (endMonth >= 12) {
    endMonth -= 12;
    endYear += 1;
  }
  return { year: endYear, month: endMonth };
}

function toDateValue(
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
  presetValue: string,
  presetLabel?: string
): DateValue {
  return {
    range: {
      from: startOfDay(new Date(startYear, startMonth)),
      to: endOfDay(endOfMonth(new Date(endYear, endMonth))),
      presetValue,
      presetLabel,
    },
  };
}

/** Returns [fromYear, fromMonth, toYear, toMonth] ensuring from <= to */
function orderedRange(ay: number, am: number, by: number, bm: number): [number, number, number, number] {
  const aBefore = ay < by || (ay === by && am <= bm);
  return aBefore ? [ay, am, by, bm] : [by, bm, ay, am];
}

export function useMonthRangeState({
  value,
  onChange,
  onCancel,
  fixedNumberOfMonth,
  hideButtons,
  maxDate,
}: MonthRangeStateOptions): MonthRangeState {
  const initialFrom = value.range.from;
  const initialTo = value.range.to;

  const [startYear, setStartYear] = useState<number>(initialFrom?.getFullYear() ?? new Date().getFullYear());
  const [startMonth, setStartMonth] = useState<number>(initialFrom?.getMonth() ?? new Date().getMonth());
  const [endYear, setEndYear] = useState<number>(initialTo?.getFullYear() ?? new Date().getFullYear());
  const [endMonth, setEndMonth] = useState<number>(initialTo?.getMonth() ?? new Date().getMonth());
  const [presetValue, setPresetValue] = useState<string>(value.range.presetValue ?? '');
  const [presetLabel, setPresetLabel] = useState<string | undefined>(value.range.presetLabel);
  const [rangePending, setRangePending] = useState<boolean>(false);
  const [menuYear, setMenuYear] = useState<number>(initialFrom?.getFullYear() ?? new Date().getFullYear());

  const [anchorYear, setAnchorYear] = useState<number>(initialFrom?.getFullYear() ?? new Date().getFullYear());
  const [anchorMonth, setAnchorMonth] = useState<number>(initialFrom?.getMonth() ?? new Date().getMonth());

  const draft: DateValue = {
    range: {
      from: startOfDay(new Date(startYear, startMonth)),
      to: endOfDay(endOfMonth(new Date(endYear, endMonth))),
      presetValue,
      presetLabel,
    },
  };

  const handleMonthClick = useCallback(
    (m: MonthCell, currentMenuYear: number) => {
      const cellYear = currentMenuYear + m.yearOffset;
      const cellMonth = m.number;

      setPresetValue('custom');
      setPresetLabel(undefined);

      if (fixedNumberOfMonth !== undefined) {
        let sy = cellYear;
        let sm = cellMonth;
        const { year: ey, month: em } = calcFixedEnd(sy, sm, fixedNumberOfMonth);

        if (maxDate) {
          const maxYear = maxDate.getFullYear();
          const maxMonth = maxDate.getMonth();
          if (ey > maxYear || (ey === maxYear && em > maxMonth)) {
            let snappedStartMonth = maxMonth - (fixedNumberOfMonth - 1);
            let snappedStartYear = maxYear;
            while (snappedStartMonth < 0) {
              snappedStartMonth += 12;
              snappedStartYear -= 1;
            }
            sy = snappedStartYear;
            sm = snappedStartMonth;
          }
        }

        const { year: finalEy, month: finalEm } = calcFixedEnd(sy, sm, fixedNumberOfMonth);
        setStartYear(sy);
        setStartMonth(sm);
        setEndYear(finalEy);
        setEndMonth(finalEm);
        setRangePending(false);

        if (hideButtons) {
          onChange(toDateValue(sy, sm, finalEy, finalEm, 'custom'));
        }
        return;
      }

      if (rangePending) {
        const [fy, fm, ty, tm] = orderedRange(anchorYear, anchorMonth, cellYear, cellMonth);
        setStartYear(fy);
        setStartMonth(fm);
        setEndYear(ty);
        setEndMonth(tm);
        setRangePending(false);

        if (hideButtons) {
          onChange(toDateValue(fy, fm, ty, tm, 'custom'));
        }
      } else {
        setAnchorYear(cellYear);
        setAnchorMonth(cellMonth);
        setStartYear(cellYear);
        setStartMonth(cellMonth);
        setEndYear(cellYear);
        setEndMonth(cellMonth);
        setRangePending(true);
      }
    },
    [anchorMonth, anchorYear, fixedNumberOfMonth, hideButtons, maxDate, onChange, rangePending]
  );

  const handleMonthHover = useCallback(
    (m: MonthCell, currentMenuYear: number) => {
      if (!rangePending) return;
      const cellYear = currentMenuYear + m.yearOffset;
      const cellMonth = m.number;

      const [fy, fm, ty, tm] = orderedRange(anchorYear, anchorMonth, cellYear, cellMonth);
      setStartYear(fy);
      setStartMonth(fm);
      setEndYear(ty);
      setEndMonth(tm);
    },
    [anchorMonth, anchorYear, rangePending]
  );

  const handlePresetChange = useCallback(
    (from: Date | undefined, to: Date | undefined, preset: string, labelKey: string) => {
      if (from) {
        setStartYear(from.getFullYear());
        setStartMonth(from.getMonth());
        setAnchorYear(from.getFullYear());
        setAnchorMonth(from.getMonth());
      }
      if (to) {
        setEndYear(to.getFullYear());
        setEndMonth(to.getMonth());
      }
      setPresetValue(preset);
      const newPresetLabel = preset !== 'custom' ? labelKey : undefined;
      setPresetLabel(newPresetLabel);
      setRangePending(false);

      if (hideButtons && preset !== 'custom' && from && to) {
        const presetLabel = labelKey;
        onChange({
          range: {
            from: startOfDay(from),
            to: endOfDay(endOfMonth(to)),
            presetValue: preset,
            presetLabel,
          },
        });
      }
    },
    [hideButtons, onChange]
  );

  const handleApply = useCallback(() => {
    onChange(toDateValue(startYear, startMonth, endYear, endMonth, presetValue, presetLabel));
  }, [endMonth, endYear, onChange, presetValue, presetLabel, startMonth, startYear]);

  const handleCancel = useCallback(() => {
    if (initialFrom) {
      setStartYear(initialFrom.getFullYear());
      setStartMonth(initialFrom.getMonth());
      setAnchorYear(initialFrom.getFullYear());
      setAnchorMonth(initialFrom.getMonth());
    }
    if (initialTo) {
      setEndYear(initialTo.getFullYear());
      setEndMonth(initialTo.getMonth());
    }
    setRangePending(false);
    onCancel();
  }, [initialFrom, initialTo, onCancel]);

  return {
    draft,
    menuYear,
    setMenuYear,
    rangePending,
    handleMonthClick,
    handleMonthHover,
    handlePresetChange,
    handleApply,
    handleCancel,
  };
}
